import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ConfirmButton from '@sayyesschool/frontend/components/confirm-button';
import CopyButton from '@sayyesschool/frontend/components/copy-button';
import LoadingIndicator from '@sayyesschool/frontend/components/loading-indicator';
import Page from '@sayyesschool/frontend/components/page';
import { useMeeting } from '@sayyesschool/frontend/features/meetings';
import { useTeachers } from '@sayyesschool/frontend/features/teachers';
import { Grid } from '@sayyesschool/frontend/ui-components';

import MeetingDetails from '@/components/meetings/meeting-details';
import MeetingRegistrations from '@/components/meetings/meeting-registrations';

export default function MeetingPage() {
    const params = useParams();
    const navigate = useNavigate();

    const [meeting, actions] = useMeeting(params.meetingId);
    const [teachers] = useTeachers();

    const handleUpdateMeeting = useCallback(data => {
        return actions.updateMeeting(meeting.id, data);
    }, [actions, meeting]);

    const handleDeleteMeeting = useCallback(() => {
        return actions.deleteMeeting(meeting.id)
            .then(() => navigate('/meetings'));
    }, [actions, navigate, meeting]);

    const handleAddRegistration = useCallback(registration => {
        return actions.addRegistration(meeting.id, registration);
    }, [actions, meeting]);

    const handleUpdateRegistration = useCallback((registration, data) => {
        return actions.updateRegistration(meeting.id, registration.id, data);
    }, [actions, meeting]);

    const handleRemoveRegistration = useCallback(registration => {
        return actions.removeRegistration(meeting.id, registration.id);
    }, [actions, meeting]);

    if (!meeting) return <LoadingIndicator />;

    return (
        <Page loading={!meeting}>
            <Page.Header
                breadcrumbs={[
                    { key: 'meetings', content: 'Встречи', to: '/meetings' }
                ]}
                title={meeting.title}
                description={meeting.zoomId && `Zoom ID: ${meeting.zoomId} / Пароль: ${meeting.password}`}
                actions={[
                    meeting.joinUrl && (
                        <CopyButton
                            key="copy"
                            title="Копировать ссылку для входа"
                            icon="link"
                            copyContent={meeting.joinUrl}
                        />
                    ),
                    <ConfirmButton
                        key="delete"
                        icon="delete"
                        title="Удалить"
                        message="Удалить встречу?"
                        color="danger"
                        onConfirm={handleDeleteMeeting}
                    />
                ]}
            />

            <Page.Content>
                <Grid gap="m">
                    <Grid.Item md={8}>
                        <MeetingDetails
                            meeting={meeting}
                            hosts={teachers}
                            onUpdate={handleUpdateMeeting}
                        />
                    </Grid.Item>

                    <Grid.Item md={4}>
                        <MeetingRegistrations
                            meeting={meeting}
                            onCreate={handleAddRegistration}
                            onUpdate={handleUpdateRegistration}
                            onDelete={handleRemoveRegistration}
                        />
                    </Grid.Item>
                </Grid>
            </Page.Content>
        </Page>
    );
}